import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { apiRequest } from '../utils/api';
import { buildOCCSymbol, roundSPXTo5Cents } from '../utils/optionSymbol';

const STRATEGY_TYPES = [
  { value: 'SINGLE', label: 'Single Option', legCount: 1 },
  { value: 'VERTICAL', label: 'Vertical Spread', legCount: 2 },
  { value: 'STRANGLE', label: 'Short Strangle', legCount: 2 },
  { value: 'IRON_CONDOR', label: 'Iron Condor', legCount: 4 },
  { value: 'CUSTOM', label: 'Custom (Manual Legs)', legCount: 0 },
];

const OPTION_INSTRUCTIONS = [
  { value: 'BUY_TO_OPEN', label: 'Buy to Open' },
  { value: 'SELL_TO_OPEN', label: 'Sell to Open' },
  { value: 'BUY_TO_CLOSE', label: 'Buy to Close' },
  { value: 'SELL_TO_CLOSE', label: 'Sell to Close' },
];

const ORDER_TYPES = [
  { value: 'LIMIT', label: 'Limit' },
  { value: 'MARKET', label: 'Market' },
  { value: 'NET_DEBIT', label: 'Net Debit' },
  { value: 'NET_CREDIT', label: 'Net Credit' },
];

export default function OptionOrderTest() {
  const [searchParams] = useSearchParams();

  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [accountsLoading, setAccountsLoading] = useState(true);

  const [strategyType, setStrategyType] = useState('SINGLE');
  const [underlying, setUnderlying] = useState('');
  const [expiration, setExpiration] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [orderType, setOrderType] = useState('LIMIT');
  const [price, setPrice] = useState('');
  const [session, setSession] = useState('NORMAL');
  const [duration, setDuration] = useState('DAY');

  // Legs for custom strategy
  const [legs, setLegs] = useState([
    { symbol: '', instruction: 'BUY_TO_OPEN', quantity: 1, putCall: 'C', strike: '', expiration: '' },
  ]);

  // For preset strategies
  const [verticalStrikes, setVerticalStrikes] = useState({ longStrike: '', shortStrike: '', putCall: 'C', direction: 'SELL' });
  const [strangleStrikes, setStrangleStrikes] = useState({ callStrike: '', putStrike: '' });
  const [ironCondorStrikes, setIronCondorStrikes] = useState({
    putLong: '', putShort: '', callShort: '', callLong: ''
  });

  // For single option strategy
  const [singleOption, setSingleOption] = useState({
    underlying: '', expiration: '', strike: '', putCall: 'C', instruction: 'BUY_TO_OPEN'
  });

  // Prefill from manual review payload passed via query params
  useEffect(() => {
    const underlyingParam = searchParams.get('underlying');
    if (!underlyingParam) return;

    const strike = searchParams.get('strike');
    const putCall = searchParams.get('putCall') || 'C';
    const expiration = searchParams.get('expiration') || '';
    const instruction = searchParams.get('instruction') || 'BUY_TO_OPEN';
    const qty = parseInt(searchParams.get('quantity')) || 1;
    const orderTypeParam = (searchParams.get('orderType') || 'LIMIT').toUpperCase();
    const priceParam = searchParams.get('price') || '';
    const tradeAccountId = searchParams.get('tradeAccountId') || '';
    const durationParam = searchParams.get('duration') || 'DAY';

    setStrategyType('SINGLE');
    setUnderlying(underlyingParam);
    setExpiration(expiration);
    setQuantity(qty);
    setOrderType(ORDER_TYPES.some(o => o.value === orderTypeParam) ? orderTypeParam : 'LIMIT');
    setPrice(orderTypeParam === 'MARKET' ? '' : priceParam);
    setDuration(durationParam);
    setSingleOption({
      underlying: underlyingParam,
      expiration,
      strike: strike || '',
      putCall,
      instruction
    });
    if (tradeAccountId) {
      setSelectedAccountId(tradeAccountId);
    }
  }, [searchParams]);

  const [result, setResult] = useState(null);
  const [previewSpec, setPreviewSpec] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  // Real-time quote state
  const [legQuotes, setLegQuotes] = useState({}); // { symbol: { bid, ask, mid, last } }
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [autoUpdatePrice, setAutoUpdatePrice] = useState(true);
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const quoteIntervalRef = useRef(null);
  const lastFetchSymbolsRef = useRef('');

  // Chains data for Greeks (delta, IV, open interest)
  const [chainsData, setChainsData] = useState({}); // { strike: { delta, iv, openInterest, putCall } }
  const [chainsLoading, setChainsLoading] = useState(false);
  const [underlyingQuote, setUnderlyingQuote] = useState(null); // { last, bid, ask, change, percentChange }

  // Fetch accounts (Schwab + Robinhood MCP)
  const fetchAccounts = useCallback(async () => {
    setAccountsLoading(true);
    try {
      // Fetch Schwab accounts
      const schwabResp = await apiRequest('/api/schwab-api/accounts');
      const schwabAccs = (schwabResp.data?.accounts || []).map(a => ({ ...a, accountType: 'schwab' }));

      // Fetch Robinhood MCP accounts
      let robinhoodAccs = [];
      try {
        const rhResp = await apiRequest('/api/mcp/robinhood/accounts');
        robinhoodAccs = (rhResp.data?.accounts || []).map(a => ({ ...a, accountType: 'robinhood-mcp' }));
      } catch (rhErr) {
        // Robinhood MCP may not be configured
      }

      // Fetch Robinhood direct-API accounts (stored OAuth token)
      let robinhoodDirectAccs = [];
      try {
        const allAccsResp = await apiRequest('/api/accounts');
        const rhDirect = (allAccsResp.data || allAccsResp || []).filter(
          a => ['robinhood', 'robinhood-api'].includes(String(a.apiType || a.api_type || '').toLowerCase())
        );
        robinhoodDirectAccs = rhDirect.map(a => ({
          id: String(a.id),
          name: a.name,
          accountType: 'robinhood',
        }));
      } catch (e) {
        // No Robinhood direct-API accounts
      }

      const allAccs = [...schwabAccs, ...robinhoodAccs, ...robinhoodDirectAccs];
      setAccounts(allAccs);
      if (allAccs.length > 0 && !selectedAccountId) {
        setSelectedAccountId(allAccs[0].id);
      }
    } catch (err) {
      setError('Failed to load accounts: ' + err.message);
    } finally {
      setAccountsLoading(false);
    }
  }, [selectedAccountId]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Fetch recent orders
  const fetchRecentOrders = useCallback(async () => {
    if (!selectedAccountId) return;
    setOrdersLoading(true);
    try {
      const selectedAcc = accounts.find(a => String(a.id) === String(selectedAccountId));
      const isRobinhoodMcp = selectedAcc?.accountType === 'robinhood-mcp';
      const isRobinhoodDirect = selectedAcc?.accountType === 'robinhood';
      let orders = [];
      if (isRobinhoodMcp) {
        const response = await apiRequest(`/api/mcp/robinhood/orders?accountId=${selectedAccountId}`);
        console.log('[OptionOrderTest] Robinhood orders response:', response.data);
        orders = response.data?.orders || [];
      } else if (isRobinhoodDirect) {
        const response = await apiRequest(`/api/robinhood-api/orders/${selectedAccountId}`);
        orders = response.data?.orders || [];
      } else {
        const response = await apiRequest(`/api/schwab-api/orders?accountId=${selectedAccountId}`);
        orders = response.data?.orders || [];
      }
      console.log('[OptionOrderTest] Fetched orders:', orders.length, orders.slice(0, 2));
      setRecentOrders(orders);
    } catch (err) {
      console.error('[OptionOrderTest] Error fetching orders:', err);
    } finally {
      setOrdersLoading(false);
    }
  }, [selectedAccountId, accounts]);

  useEffect(() => {
    if (selectedAccountId) {
      fetchRecentOrders();
    }
  }, [selectedAccountId, fetchRecentOrders]);

  // Build legs based on strategy (also used for quote polling)
  const buildLegs = useCallback(() => {
    const qty = parseInt(quantity) || 1;

    if (strategyType === 'SINGLE') {
      if (!singleOption.underlying || !singleOption.expiration || !singleOption.strike) return null;
      const sym = buildOCCSymbol(singleOption.underlying, singleOption.expiration, singleOption.putCall, parseFloat(singleOption.strike));
      return [{ symbol: sym, instruction: singleOption.instruction, quantity: qty }];
    }

    if (strategyType === 'VERTICAL') {
      const { longStrike, shortStrike, putCall, direction } = verticalStrikes;
      if (!longStrike || !shortStrike || !underlying || !expiration) return null;
      const longSym = buildOCCSymbol(underlying, expiration, putCall, parseFloat(longStrike));
      const shortSym = buildOCCSymbol(underlying, expiration, putCall, parseFloat(shortStrike));
      const isCredit = direction === 'SELL';
      return [
        { symbol: shortSym, instruction: isCredit ? 'SELL_TO_OPEN' : 'BUY_TO_OPEN', quantity: qty },
        { symbol: longSym, instruction: isCredit ? 'BUY_TO_OPEN' : 'SELL_TO_OPEN', quantity: qty },
      ];
    }

    if (strategyType === 'STRANGLE') {
      const { callStrike, putStrike } = strangleStrikes;
      if (!callStrike || !putStrike || !underlying || !expiration) return null;
      const callSym = buildOCCSymbol(underlying, expiration, 'C', parseFloat(callStrike));
      const putSym = buildOCCSymbol(underlying, expiration, 'P', parseFloat(putStrike));
      return [
        { symbol: callSym, instruction: 'SELL_TO_OPEN', quantity: qty },
        { symbol: putSym, instruction: 'SELL_TO_OPEN', quantity: qty },
      ];
    }

    if (strategyType === 'IRON_CONDOR') {
      const { putLong, putShort, callShort, callLong } = ironCondorStrikes;
      if (!putLong || !putShort || !callShort || !callLong || !underlying || !expiration) return null;
      const putLongSym = buildOCCSymbol(underlying, expiration, 'P', parseFloat(putLong));
      const putShortSym = buildOCCSymbol(underlying, expiration, 'P', parseFloat(putShort));
      const callShortSym = buildOCCSymbol(underlying, expiration, 'C', parseFloat(callShort));
      const callLongSym = buildOCCSymbol(underlying, expiration, 'C', parseFloat(callLong));
      return [
        { symbol: putLongSym, instruction: 'BUY_TO_OPEN', quantity: qty },
        { symbol: putShortSym, instruction: 'SELL_TO_OPEN', quantity: qty },
        { symbol: callShortSym, instruction: 'SELL_TO_OPEN', quantity: qty },
        { symbol: callLongSym, instruction: 'BUY_TO_OPEN', quantity: qty },
      ];
    }

    if (strategyType === 'CUSTOM') {
      return legs
        .filter(l => l.symbol)
        .map(l => ({ symbol: l.symbol, instruction: l.instruction, quantity: parseInt(l.quantity) || qty }));
    }

    return null;
  }, [strategyType, legs, quantity, verticalStrikes, strangleStrikes, ironCondorStrikes, underlying, expiration, singleOption]);

  // Calculate net mid price from quotes and leg directions
  const calculateNetMid = useCallback((builtLegs, quotes) => {
    if (!builtLegs || !quotes) return null;
    let netMid = 0;
    let allFound = true;
    for (const leg of builtLegs) {
      // Backend returns quote keys with spaces stripped
      const symKey = leg.symbol.replace(/\s+/g, '');
      const q = quotes[symKey];
      if (!q || (!q.bid && !q.ask)) {
        allFound = false;
        continue;
      }
      const isBuy = leg.instruction === 'BUY_TO_OPEN' || leg.instruction === 'BUY_TO_CLOSE';
      const roundedMid = roundSPXTo5Cents(q.mid, isBuy, q.underlyingSymbol || leg.symbol);
      netMid += isBuy ? roundedMid : -roundedMid;
    }
    if (!allFound) return null;
    return netMid;
  }, []);

  // Fetch quotes for current leg symbols
  const fetchQuotes = useCallback(async () => {
    if (!selectedAccountId || orderSubmitted) return;
    // Always use a Schwab account for quotes (market data)
    const schwabAcc = accounts.find(a => a.accountType === 'schwab');
    const quoteAccountId = schwabAcc?.id || selectedAccountId;
    const builtLegs = buildLegs();
    if (!builtLegs || builtLegs.length === 0) {
      setLegQuotes({});
      return;
    }
    const symbols = builtLegs.map(l => l.symbol);
    if (!symbols || symbols.length === 0) {
      setLegQuotes({});
      return;
    }
    setQuotesLoading(true);
    try {
      const response = await apiRequest('/api/schwab-api/quotes', {
        method: 'POST',
        body: JSON.stringify({ accountId: quoteAccountId, symbols }),
      });
      const quotes = response.data?.quotes || {};
      setLegQuotes(quotes);
      setQuoteError(null);

      // Auto-fill price from net mid
      if (autoUpdatePrice && (orderType === 'LIMIT' || orderType === 'NET_DEBIT' || orderType === 'NET_CREDIT')) {
        const netMid = calculateNetMid(builtLegs, quotes);
        if (netMid !== null) {
          const rounded = Math.round(Math.abs(netMid) * 100) / 100;
          setPrice(String(rounded));
        }
      }
    } catch (err) {
      setQuoteError(err.message || 'Failed to fetch quotes');
    } finally {
      setQuotesLoading(false);
    }
  }, [selectedAccountId, buildLegs, calculateNetMid, autoUpdatePrice, orderType, orderSubmitted]);

  // Poll quotes every 1 second
  useEffect(() => {
    if (orderSubmitted) {
      if (quoteIntervalRef.current) {
        clearInterval(quoteIntervalRef.current);
        quoteIntervalRef.current = null;
      }
      return;
    }

    // Initial fetch
    fetchQuotes();

    // Set up 1-second interval
    quoteIntervalRef.current = setInterval(fetchQuotes, 1000);

    return () => {
      if (quoteIntervalRef.current) {
        clearInterval(quoteIntervalRef.current);
        quoteIntervalRef.current = null;
      }
    };
  }, [fetchQuotes, orderSubmitted]);

  // Fetch chains data for Greeks (delta, IV, open interest)
  const fetchChainsData = useCallback(async () => {
    if (!selectedAccountId) {
      setChainsData({});
      return;
    }
    // Always use a Schwab account for chains (market data)
    const schwabAcc = accounts.find(a => a.accountType === 'schwab');
    const quoteAccountId = schwabAcc?.id || selectedAccountId;

    // Determine which underlying and expiration to use based on strategy
    let currentUnderlying = underlying;
    let currentExpiration = expiration;
    
    if (strategyType === 'SINGLE') {
      currentUnderlying = singleOption.underlying;
      currentExpiration = singleOption.expiration;
    }

    if (!currentUnderlying || !currentExpiration) {
      setChainsData({});
      return;
    }

    setChainsLoading(true);
    try {
      // Convert expiration from date format (YYYY-MM-DD) or YYMMDD to YYYY-MM-DD format
      let expDate = '';
      if (currentExpiration.length === 6) {
        // YYMMDD format
        const year = 2000 + parseInt(currentExpiration.substring(0, 2));
        const month = currentExpiration.substring(2, 4);
        const day = currentExpiration.substring(4, 6);
        expDate = `${year}-${month}-${day}`;
      } else if (currentExpiration.includes('-')) {
        // Already in YYYY-MM-DD format
        expDate = currentExpiration;
      }

      // Map SPX/SPXW to $SPX for Schwab API
      let symbol = currentUnderlying;
      if (symbol === 'SPXW' || symbol === 'SPX') {
        symbol = '$SPX';
      }

      // Fetch both call and put chains
      const [callResponse, putResponse] = await Promise.all([
        apiRequest(`/api/schwab-api/chains?accountId=${quoteAccountId}&symbol=${symbol}&contractType=CALL&includeUnderlyingQuote=true&strategy=SINGLE&fromDate=${expDate}&toDate=${expDate}`),
        apiRequest(`/api/schwab-api/chains?accountId=${quoteAccountId}&symbol=${symbol}&contractType=PUT&includeUnderlyingQuote=true&strategy=SINGLE&fromDate=${expDate}&toDate=${expDate}`)
      ]);

      const chainsMap = {};

      // Process call chains
      if (callResponse.data?.data?.callExpDateMap) {
        for (const [expKey, strikes] of Object.entries(callResponse.data.data.callExpDateMap)) {
          for (const [strike, options] of Object.entries(strikes)) {
            if (options && options.length > 0) {
              const opt = options[0];
              chainsMap[strike] = {
                ...chainsMap[strike],
                callDelta: opt.delta,
                callIV: opt.volatility,
                callOI: opt.openInterest,
                callMark: opt.mark,
                callBid: opt.bid,
                callAsk: opt.ask
              };
            }
          }
        }
      }

      // Extract underlying quote from response
      if (callResponse.data?.data?.underlying) {
        const underlying = callResponse.data.data.underlying;
        setUnderlyingQuote({
          last: underlying.last,
          bid: underlying.bid,
          ask: underlying.ask,
          change: underlying.netChange,
          percentChange: underlying.netPercentChange
        });
      }

      // Process put chains
      if (putResponse.data?.data?.putExpDateMap) {
        for (const [expKey, strikes] of Object.entries(putResponse.data.data.putExpDateMap)) {
          for (const [strike, options] of Object.entries(strikes)) {
            if (options && options.length > 0) {
              const opt = options[0];
              chainsMap[strike] = {
                ...chainsMap[strike],
                putDelta: opt.delta,
                putIV: opt.volatility,
                putOI: opt.openInterest,
                putMark: opt.mark,
                putBid: opt.bid,
                putAsk: opt.ask
              };
            }
          }
        }
      }

      setChainsData(chainsMap);
    } catch (err) {
      console.error('Failed to fetch chains data:', err);
      setChainsData({});
    } finally {
      setChainsLoading(false);
    }
  }, [selectedAccountId, underlying, expiration, strategyType, singleOption.underlying, singleOption.expiration]);

  // Fetch chains when underlying or expiration changes
  useEffect(() => {
    if ((strategyType === 'SINGLE' && singleOption.underlying && singleOption.expiration) ||
        (strategyType !== 'SINGLE' && underlying && expiration)) {
      fetchChainsData();
    }
  }, [fetchChainsData, strategyType, singleOption.underlying, singleOption.expiration]);

  const handlePreview = async () => {
    setError(null);
    setResult(null);
    setPreviewSpec(null);

    const builtLegs = buildLegs();
    if (!builtLegs || builtLegs.length === 0) {
      setError('Please fill in all required fields for the strategy');
      return;
    }

    setLoading(true);
    try {
      const isPricedType = orderType === 'LIMIT' || orderType === 'NET_DEBIT' || orderType === 'NET_CREDIT';
      const body = {
        legs: builtLegs,
        orderType,
        price: isPricedType ? parseFloat(price) : undefined,
        session,
        duration,
      };
      const response = await apiRequest('/api/schwab-api/orders/preview', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setPreviewSpec(response.data?.orderSpec);
    } catch (err) {
      setError('Preview failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceOrder = async () => {
    setError(null);
    setResult(null);
    setPreviewSpec(null);

    if (!selectedAccountId) {
      setError('Please select an account');
      return;
    }

    const builtLegs = buildLegs();
    if (!builtLegs || builtLegs.length === 0) {
      setError('Please fill in all required fields for the strategy');
      return;
    }

    setOrderSubmitted(true);
    setLoading(true);
    try {
      // For multi-leg spreads, Schwab uses NET_DEBIT or NET_CREDIT instead of LIMIT
      let effectiveOrderType = orderType;
      let effectivePrice = orderType === 'LIMIT' || orderType === 'NET_DEBIT' || orderType === 'NET_CREDIT' ? parseFloat(price) : undefined;

      if (builtLegs.length > 1 && (orderType === 'LIMIT' || orderType === 'MARKET')) {
        // Auto-determine debit vs credit from net mid
        const netMid = calculateNetMid(builtLegs, legQuotes);
        if (netMid !== null) {
          effectiveOrderType = netMid >= 0 ? 'NET_DEBIT' : 'NET_CREDIT';
          // Only auto-calculate price if autoUpdatePrice is on; respect manual price otherwise
          if (autoUpdatePrice) {
            effectivePrice = Math.abs(Math.round(netMid * 100) / 100);
          }
        } else {
          effectiveOrderType = 'NET_DEBIT';
        }
      }

      const body = {
        accountId: selectedAccountId,
        legs: builtLegs,
        orderType: effectiveOrderType,
        price: effectivePrice,
        session,
        duration,
      };
      // Determine account type and route to correct API
      const selectedAcc = accounts.find(a => String(a.id) === String(selectedAccountId));
      const isRobinhoodMcp = selectedAcc?.accountType === 'robinhood-mcp';
      const isRobinhoodDirect = selectedAcc?.accountType === 'robinhood';

      let response;
      if (isRobinhoodMcp) {
        const rhBody = {
          accountId: selectedAccountId,
          legs: builtLegs,
          orderType: effectiveOrderType,
          price: effectivePrice,
          timeInForce: 'gfd',
        };
        response = await apiRequest('/api/mcp/robinhood/orders/place', {
          method: 'POST',
          body: JSON.stringify(rhBody),
        });
      } else if (isRobinhoodDirect) {
        const rhDirectBody = {
          accountId: selectedAccountId,
          legs: builtLegs,
          orderType: effectiveOrderType,
          price: effectivePrice,
          timeInForce: 'gfd',
        };
        response = await apiRequest(`/api/robinhood-api/orders/place/${selectedAccountId}`, {
          method: 'POST',
          body: JSON.stringify(rhDirectBody),
        });
      } else {
        response = await apiRequest('/api/schwab-api/orders/place', {
          method: 'POST',
          body: JSON.stringify(body),
        });
      }
      setResult(response.data);
      if (response.data?.success) {
        fetchRecentOrders();
      } else {
        const detail = response.data?.message || 'Unknown error';
        const status = response.data?.status ? ` (HTTP ${response.data.status})` : '';
        setError(`Order placement failed: ${detail}${status}`);
      }
    } catch (err) {
      setError('Order placement failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
      setOrderSubmitted(false);
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (!selectedAccountId || !orderId) return;
    try {
      const selectedAcc = accounts.find(a => String(a.id) === String(selectedAccountId));
      const isRobinhoodMcp = selectedAcc?.accountType === 'robinhood-mcp';
      const isRobinhoodDirect = selectedAcc?.accountType === 'robinhood';
      if (isRobinhoodMcp) {
        await apiRequest(`/api/mcp/robinhood/orders/${orderId}/cancel`, {
          method: 'POST',
        });
      } else if (isRobinhoodDirect) {
        setError('Cancel not supported for Robinhood direct-API accounts');
        return;
      } else {
        await apiRequest(`/api/schwab-api/orders/${orderId}`, {
          method: 'DELETE',
          body: JSON.stringify({ accountId: selectedAccountId }),
        });
      }
      fetchRecentOrders();
    } catch (err) {
      setError('Cancel failed: ' + (err.message || 'Unknown error'));
    }
  };

  // Modify order state
  const [modifyingOrderId, setModifyingOrderId] = useState(null);
  const [modifyPrice, setModifyPrice] = useState('');
  const [modifyLoading, setModifyLoading] = useState(false);

  const startModifyOrder = (order) => {
    const orderId = order.orderId || order.id;
    setModifyingOrderId(orderId);
    setModifyPrice(String(order.price || order.activePrice || ''));
  };

  const cancelModifyOrder = () => {
    setModifyingOrderId(null);
    setModifyPrice('');
  };

  const handleModifyOrder = async (orderId) => {
    if (!selectedAccountId || !orderId) return;
    const builtLegs = buildLegs();
    if (!builtLegs) {
      setError('Cannot modify — leg configuration is incomplete');
      return;
    }

    setModifyLoading(true);
    try {
      // For multi-leg spreads, auto-determine NET_DEBIT/NET_CREDIT (same as place order)
      let effectiveOrderType = orderType;
      let effectivePrice = (orderType === 'LIMIT' || orderType === 'NET_DEBIT' || orderType === 'NET_CREDIT') ? parseFloat(modifyPrice) : undefined;

      if (builtLegs.length > 1 && (orderType === 'LIMIT' || orderType === 'MARKET')) {
        const netMid = calculateNetMid(builtLegs, legQuotes);
        if (netMid !== null) {
          effectiveOrderType = netMid >= 0 ? 'NET_DEBIT' : 'NET_CREDIT';
          // Only auto-calculate price if user didn't manually enter one
          if (!effectivePrice) {
            effectivePrice = Math.abs(Math.round(netMid * 100) / 100);
          }
        } else {
          effectiveOrderType = 'NET_DEBIT';
        }
      }

      const body = {
        accountId: selectedAccountId,
        legs: builtLegs,
        orderType: effectiveOrderType,
        price: effectivePrice,
        session,
        duration,
      };
      const response = await apiRequest(`/api/schwab-api/orders/${orderId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      if (response.data?.success) {
        cancelModifyOrder();
        fetchRecentOrders();
      } else {
        setError('Modify failed: ' + (response.data?.message || 'Unknown error'));
      }
    } catch (err) {
      setError('Modify failed: ' + (err.message || 'Unknown error'));
    } finally {
      setModifyLoading(false);
    }
  };

  // Leg management for custom strategy
  const addLeg = () => {
    setLegs([...legs, { symbol: '', instruction: 'BUY_TO_OPEN', quantity: 1, putCall: 'C', strike: '', expiration: '' }]);
  };

  const removeLeg = (idx) => {
    setLegs(legs.filter((_, i) => i !== idx));
  };

  const updateLeg = (idx, field, value) => {
    const updated = [...legs];
    updated[idx] = { ...updated[idx], [field]: value };
    setLegs(updated);
  };

  const updateLegSymbol = (idx, value) => {
    const updated = [...legs];
    updated[idx] = { ...updated[idx], symbol: value };
    setLegs(updated);
  };

  const needsUnderlyingAndExpiration = ['VERTICAL', 'STRANGLE', 'IRON_CONDOR'].includes(strategyType);

  return (
    <Box sx={{ p: { xs: 1, sm: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Test placing option orders via Schwab Trader API. Supports single options, vertical spreads, short strangles, and iron condors.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {result && (
        <Alert
          severity={result.success ? 'success' : 'error'}
          sx={{ mb: 2 }}
          onClose={() => setResult(null)}
        >
          {result.success
            ? `Order placed successfully! Order ID: ${result.orderId || 'N/A'}`
            : `Order failed: ${result.message || 'Unknown error'}`}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left column: Order form */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Configuration
              </Typography>

              {/* Account selection */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Account</InputLabel>
                <Select
                  value={selectedAccountId}
                  label="Account"
                  onChange={(e) => setSelectedAccountId(e.target.value)}
                  disabled={accountsLoading}
                >
                  {accounts.map((acc) => (
                    <MenuItem key={acc.id} value={acc.id}>
                      {acc.name} {acc.accountType ? `(${acc.accountType === 'schwab' ? 'Schwab' : acc.accountType === 'robinhood-mcp' ? 'Robinhood MCP' : acc.accountType === 'robinhood' ? 'Robinhood' : acc.accountType})` : (acc.accountHash ? `(${acc.accountHash.slice(0, 8)}...)` : '')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Strategy type */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Strategy Type</InputLabel>
                <Select
                  value={strategyType}
                  label="Strategy Type"
                  onChange={(e) => {
                    setStrategyType(e.target.value);
                    setResult(null);
                    setPreviewSpec(null);
                  }}
                >
                  {STRATEGY_TYPES.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Common fields for preset strategies */}
              {needsUnderlyingAndExpiration && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Underlying Symbol"
                      value={underlying}
                      onChange={(e) => setUnderlying(e.target.value.toUpperCase())}
                      placeholder="e.g. SPY"
                    />
                    {underlyingQuote && (
                      <Box sx={{ mt: 0.5, fontSize: '0.75rem', color: '#666' }}>
                        <span style={{ fontWeight: 'bold' }}>${underlyingQuote.last?.toFixed(2)}</span>
                        {' '}B: ${underlyingQuote.bid?.toFixed(2)} A: ${underlyingQuote.ask?.toFixed(2)}
                        {' '}({underlyingQuote.change >= 0 ? '+' : ''}{underlyingQuote.change?.toFixed(2)} / {underlyingQuote.percentChange >= 0 ? '+' : ''}{underlyingQuote.percentChange?.toFixed(2)}%)
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Expiration Date"
                      value={expiration}
                      onChange={(e) => setExpiration(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              )}

              {/* Vertical spread fields */}
              {strategyType === 'VERTICAL' && (
                <Box sx={{ mb: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Long Strike"
                        value={verticalStrikes.longStrike}
                        onChange={(e) => setVerticalStrikes({ ...verticalStrikes, longStrike: e.target.value })}
                        placeholder="e.g. 150"
                      />
                      {verticalStrikes.longStrike && chainsData[verticalStrikes.longStrike] && (
                        <Box sx={{ mt: 0.5, fontSize: '0.7rem', color: '#666' }}>
                          Δ: {(chainsData[verticalStrikes.longStrike][verticalStrikes.putCall === 'C' ? 'callDelta' : 'putDelta'] || 0).toFixed(3)} | 
                          IV: {(chainsData[verticalStrikes.longStrike][verticalStrikes.putCall === 'C' ? 'callIV' : 'putIV'] || 0).toFixed(1)}% | 
                          OI: {(chainsData[verticalStrikes.longStrike][verticalStrikes.putCall === 'C' ? 'callOI' : 'putOI'] || 0).toLocaleString()}
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Short Strike"
                        value={verticalStrikes.shortStrike}
                        onChange={(e) => setVerticalStrikes({ ...verticalStrikes, shortStrike: e.target.value })}
                        placeholder="e.g. 155"
                      />
                      {verticalStrikes.shortStrike && chainsData[verticalStrikes.shortStrike] && (
                        <Box sx={{ mt: 0.5, fontSize: '0.7rem', color: '#666' }}>
                          Δ: {(chainsData[verticalStrikes.shortStrike][verticalStrikes.putCall === 'C' ? 'callDelta' : 'putDelta'] || 0).toFixed(3)} | 
                          IV: {(chainsData[verticalStrikes.shortStrike][verticalStrikes.putCall === 'C' ? 'callIV' : 'putIV'] || 0).toFixed(1)}% | 
                          OI: {(chainsData[verticalStrikes.shortStrike][verticalStrikes.putCall === 'C' ? 'callOI' : 'putOI'] || 0).toLocaleString()}
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={2}>
                      <FormControl fullWidth>
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={verticalStrikes.putCall}
                          label="Type"
                          onChange={(e) => setVerticalStrikes({ ...verticalStrikes, putCall: e.target.value })}
                        >
                          <MenuItem value="C">Call</MenuItem>
                          <MenuItem value="P">Put</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={2}>
                      <FormControl fullWidth>
                        <InputLabel>Dir</InputLabel>
                        <Select
                          value={verticalStrikes.direction}
                          label="Dir"
                          onChange={(e) => setVerticalStrikes({ ...verticalStrikes, direction: e.target.value })}
                        >
                          <MenuItem value="SELL">Credit</MenuItem>
                          <MenuItem value="BUY">Debit</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Strangle fields */}
              {strategyType === 'STRANGLE' && (
                <Box sx={{ mb: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Call Strike (Sell)"
                        value={strangleStrikes.callStrike}
                        onChange={(e) => setStrangleStrikes({ ...strangleStrikes, callStrike: e.target.value })}
                        placeholder="e.g. 160"
                      />
                      {strangleStrikes.callStrike && chainsData[strangleStrikes.callStrike] && (
                        <Box sx={{ mt: 0.5, fontSize: '0.7rem', color: '#666' }}>
                          Δ: {(chainsData[strangleStrikes.callStrike].callDelta || 0).toFixed(3)} | 
                          IV: {(chainsData[strangleStrikes.callStrike].callIV || 0).toFixed(1)}% | 
                          OI: {(chainsData[strangleStrikes.callStrike].callOI || 0).toLocaleString()}
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Put Strike (Sell)"
                        value={strangleStrikes.putStrike}
                        onChange={(e) => setStrangleStrikes({ ...strangleStrikes, putStrike: e.target.value })}
                        placeholder="e.g. 140"
                      />
                      {strangleStrikes.putStrike && chainsData[strangleStrikes.putStrike] && (
                        <Box sx={{ mt: 0.5, fontSize: '0.7rem', color: '#666' }}>
                          Δ: {(chainsData[strangleStrikes.putStrike].putDelta || 0).toFixed(3)} | 
                          IV: {(chainsData[strangleStrikes.putStrike].putIV || 0).toFixed(1)}% | 
                          OI: {(chainsData[strangleStrikes.putStrike].putOI || 0).toLocaleString()}
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Iron Condor fields */}
              {strategyType === 'IRON_CONDOR' && (
                <Box sx={{ mb: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="Put Long (Buy)"
                        value={ironCondorStrikes.putLong}
                        onChange={(e) => setIronCondorStrikes({ ...ironCondorStrikes, putLong: e.target.value })}
                        placeholder="e.g. 135"
                      />
                      {ironCondorStrikes.putLong && chainsData[ironCondorStrikes.putLong] && (
                        <Box sx={{ mt: 0.5, fontSize: '0.7rem', color: '#666' }}>
                          Δ: {(chainsData[ironCondorStrikes.putLong].putDelta || 0).toFixed(3)} | 
                          IV: {(chainsData[ironCondorStrikes.putLong].putIV || 0).toFixed(1)}% | 
                          OI: {(chainsData[ironCondorStrikes.putLong].putOI || 0).toLocaleString()}
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="Put Short (Sell)"
                        value={ironCondorStrikes.putShort}
                        onChange={(e) => setIronCondorStrikes({ ...ironCondorStrikes, putShort: e.target.value })}
                        placeholder="e.g. 140"
                      />
                      {ironCondorStrikes.putShort && chainsData[ironCondorStrikes.putShort] && (
                        <Box sx={{ mt: 0.5, fontSize: '0.7rem', color: '#666' }}>
                          Δ: {(chainsData[ironCondorStrikes.putShort].putDelta || 0).toFixed(3)} | 
                          IV: {(chainsData[ironCondorStrikes.putShort].putIV || 0).toFixed(1)}% | 
                          OI: {(chainsData[ironCondorStrikes.putShort].putOI || 0).toLocaleString()}
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="Call Short (Sell)"
                        value={ironCondorStrikes.callShort}
                        onChange={(e) => setIronCondorStrikes({ ...ironCondorStrikes, callShort: e.target.value })}
                        placeholder="e.g. 160"
                      />
                      {ironCondorStrikes.callShort && chainsData[ironCondorStrikes.callShort] && (
                        <Box sx={{ mt: 0.5, fontSize: '0.7rem', color: '#666' }}>
                          Δ: {(chainsData[ironCondorStrikes.callShort].callDelta || 0).toFixed(3)} | 
                          IV: {(chainsData[ironCondorStrikes.callShort].callIV || 0).toFixed(1)}% | 
                          OI: {(chainsData[ironCondorStrikes.callShort].callOI || 0).toLocaleString()}
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="Call Long (Buy)"
                        value={ironCondorStrikes.callLong}
                        onChange={(e) => setIronCondorStrikes({ ...ironCondorStrikes, callLong: e.target.value })}
                        placeholder="e.g. 165"
                      />
                      {ironCondorStrikes.callLong && chainsData[ironCondorStrikes.callLong] && (
                        <Box sx={{ mt: 0.5, fontSize: '0.7rem', color: '#666' }}>
                          Δ: {(chainsData[ironCondorStrikes.callLong].callDelta || 0).toFixed(3)} | 
                          IV: {(chainsData[ironCondorStrikes.callLong].callIV || 0).toFixed(1)}% | 
                          OI: {(chainsData[ironCondorStrikes.callLong].callOI || 0).toLocaleString()}
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Single option fields */}
              {strategyType === 'SINGLE' && (
                <Box sx={{ mb: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="Underlying"
                        value={singleOption.underlying}
                        onChange={(e) => setSingleOption({ ...singleOption, underlying: e.target.value.toUpperCase() })}
                        placeholder="e.g. SPY"
                      />
                      {underlyingQuote && (
                        <Box sx={{ mt: 0.5, fontSize: '0.7rem', color: '#666' }}>
                          <span style={{ fontWeight: 'bold' }}>${underlyingQuote.last?.toFixed(2)}</span>
                          {' '}B: ${underlyingQuote.bid?.toFixed(2)} A: ${underlyingQuote.ask?.toFixed(2)}
                          {' '}({underlyingQuote.change >= 0 ? '+' : ''}{underlyingQuote.change?.toFixed(2)} / {underlyingQuote.percentChange >= 0 ? '+' : ''}{underlyingQuote.percentChange?.toFixed(2)}%)
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Expiration"
                        value={singleOption.expiration}
                        onChange={(e) => setSingleOption({ ...singleOption, expiration: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <TextField
                        fullWidth
                        label="Strike"
                        value={singleOption.strike}
                        onChange={(e) => setSingleOption({ ...singleOption, strike: e.target.value })}
                        placeholder="e.g. 150"
                      />
                      {singleOption.strike && chainsData[singleOption.strike] && (
                        <Box sx={{ mt: 0.5, fontSize: '0.7rem', color: '#666' }}>
                          Δ: {(chainsData[singleOption.strike][singleOption.putCall === 'C' ? 'callDelta' : 'putDelta'] || 0).toFixed(3)} | 
                          IV: {(chainsData[singleOption.strike][singleOption.putCall === 'C' ? 'callIV' : 'putIV'] || 0).toFixed(1)}% | 
                          OI: {(chainsData[singleOption.strike][singleOption.putCall === 'C' ? 'callOI' : 'putOI'] || 0).toLocaleString()}
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={2}>
                      <FormControl fullWidth>
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={singleOption.putCall}
                          label="Type"
                          onChange={(e) => setSingleOption({ ...singleOption, putCall: e.target.value })}
                        >
                          <MenuItem value="C">Call</MenuItem>
                          <MenuItem value="P">Put</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={2}>
                      <FormControl fullWidth>
                        <InputLabel>Side</InputLabel>
                        <Select
                          value={singleOption.instruction}
                          label="Side"
                          onChange={(e) => setSingleOption({ ...singleOption, instruction: e.target.value })}
                        >
                          {OPTION_INSTRUCTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Custom legs */}
              {strategyType === 'CUSTOM' && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Order Legs
                  </Typography>
                  {legs.map((leg, idx) => (
                    <Grid container spacing={1} key={idx} sx={{ mb: 1 }} alignItems="center">
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label={`Leg ${idx + 1} Symbol (OCC)`}
                          value={leg.symbol}
                          onChange={(e) => updateLegSymbol(idx, e.target.value.toUpperCase())}
                          placeholder="e.g. SPY240315C00150000"
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Instruction</InputLabel>
                          <Select
                            value={leg.instruction}
                            label="Instruction"
                            onChange={(e) => updateLeg(idx, 'instruction', e.target.value)}
                          >
                            {OPTION_INSTRUCTIONS.map((o) => (
                              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Qty"
                          value={leg.quantity}
                          onChange={(e) => updateLeg(idx, 'quantity', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton size="small" onClick={() => removeLeg(idx)} disabled={legs.length <= 1}>
                          <RemoveIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                  <Button startIcon={<AddIcon />} onClick={addLeg} size="small" sx={{ mt: 1 }}>
                    Add Leg
                  </Button>
                </Box>
              )}

              {/* Real-time quotes display */}
              {(() => {
    const builtLegs = buildLegs();
    if (!builtLegs || builtLegs.length === 0) return null;
    return (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="subtitle2">
                        Live Quotes
                      </Typography>
                      {quotesLoading && <CircularProgress size={14} />}
                    </Box>
                    {quoteError && (
                      <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setQuoteError(null)}>
                        {quoteError}
                      </Alert>
                    )}
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Leg</TableCell>
                            <TableCell>Symbol</TableCell>
                            <TableCell align="right">Bid</TableCell>
                            <TableCell align="right">Ask</TableCell>
                            <TableCell align="right">Mid</TableCell>
                            <TableCell align="right">Last</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {builtLegs.map((leg, idx) => {
                            const symKey = leg.symbol.replace(/\s+/g, '');
                            const q = legQuotes[symKey];
                            const isBuy = leg.instruction === 'BUY_TO_OPEN' || leg.instruction === 'BUY_TO_CLOSE';
                            return (
                              <TableRow key={idx}>
                                <TableCell>
                                  <Chip
                                    label={isBuy ? 'BUY' : 'SELL'}
                                    size="small"
                                    color={isBuy ? 'primary' : 'secondary'}
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                  {leg.symbol}
                                </TableCell>
                                <TableCell align="right" sx={{ color: q?.bid ? 'text.primary' : 'text.disabled' }}>
                                  {q?.bid ? q.bid.toFixed(2) : '—'}
                                </TableCell>
                                <TableCell align="right" sx={{ color: q?.ask ? 'text.primary' : 'text.disabled' }}>
                                  {q?.ask ? q.ask.toFixed(2) : '—'}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', color: q?.mid ? 'primary.main' : 'text.disabled' }}>
                                  {q?.mid ? q.mid.toFixed(2) : '—'}
                                </TableCell>
                                <TableCell align="right" sx={{ color: q?.last ? 'text.secondary' : 'text.disabled' }}>
                                  {q?.last ? q.last.toFixed(2) : '—'}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          {(() => {
                            const netMid = calculateNetMid(builtLegs, legQuotes);
                            return (
                              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                <TableCell colSpan={4}>
                                  <Typography variant="subtitle2">Net Mid (auto-filled price)</Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem', color: 'primary.main' }}>
                                  {netMid !== null ? netMid.toFixed(2) : '—'}
                                </TableCell>
                                <TableCell />
                              </TableRow>
                            );
                          })()}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                );
              })()}

              <Divider sx={{ my: 2 }} />

              {/* Order parameters */}
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Quantity (contracts)"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                  />
                </Grid>
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <InputLabel>Order Type</InputLabel>
                    <Select
                      value={orderType}
                      label="Order Type"
                      onChange={(e) => setOrderType(e.target.value)}
                    >
                      {ORDER_TYPES.map((o) => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Limit Price"
                    value={price}
                    onChange={(e) => {
                      setPrice(e.target.value);
                      setAutoUpdatePrice(false);
                    }}
                    disabled={orderType !== 'LIMIT' && orderType !== 'NET_DEBIT' && orderType !== 'NET_CREDIT'}
                    placeholder="e.g. 1.50"
                    helperText={(orderType === 'LIMIT' || orderType === 'NET_DEBIT' || orderType === 'NET_CREDIT') ? (autoUpdatePrice ? 'Auto from mid' : 'Manual') : ''}
                    slotProps={(orderType === 'LIMIT' || orderType === 'NET_DEBIT' || orderType === 'NET_CREDIT') ? {
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Tooltip title={autoUpdatePrice ? 'Auto-update from mid. Click to lock manual price.' : 'Manual price locked. Click to auto-update from mid.'}>
                              <IconButton
                                size="small"
                                sx={{ p: 0.5 }}
                                onClick={() => setAutoUpdatePrice(!autoUpdatePrice)}
                              >
                                {autoUpdatePrice ? <LockOpenIcon fontSize="small" /> : <LockIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <IconButton
                            size="small"
                            sx={{ p: 0.25 }}
                            onClick={() => {
                              const val = parseFloat(price) || 0;
                              setPrice(String(Math.round((val + 0.01) * 100) / 100));
                              setAutoUpdatePrice(false);
                            }}
                          >
                            <ArrowDropUpIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            sx={{ p: 0.25, mt: -1 }}
                            onClick={() => {
                              const val = parseFloat(price) || 0;
                              setPrice(String(Math.round((val - 0.01) * 100) / 100));
                              setAutoUpdatePrice(false);
                            }}
                          >
                            <ArrowDropDownIcon />
                          </IconButton>
                        </Box>
                          </InputAdornment>
                        ),
                      }
                    } : undefined}
                  />
                </Grid>
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <InputLabel>Duration</InputLabel>
                    <Select
                      value={duration}
                      label="Duration"
                      onChange={(e) => setDuration(e.target.value)}
                    >
                      <MenuItem value="DAY">Day</MenuItem>
                      <MenuItem value="GOOD_TILL_CANCEL">GTC</MenuItem>
                      <MenuItem value="FILL_OR_KILL">FOK</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Action buttons */}
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={handlePreview}
                  disabled={loading}
                >
                  Preview Order Spec
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                  onClick={handlePlaceOrder}
                  disabled={loading || !selectedAccountId}
                >
                  Place Order
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Preview */}
          {previewSpec && (
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">
                  Order Spec Preview (JSON sent to Schwab)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', overflowX: 'auto' }}>
                  <pre style={{ fontSize: '0.85rem', margin: 0 }}>
                    {JSON.stringify(previewSpec, null, 2)}
                  </pre>
                </Paper>
              </AccordionDetails>
            </Accordion>
          )}
        </Grid>

        {/* Right column: Recent orders */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Recent Orders
                </Typography>
                <Tooltip title="Refresh">
                  <IconButton size="small" onClick={fetchRecentOrders} disabled={ordersLoading}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              {ordersLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : recentOrders.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
                  No recent orders found
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Order ID</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Price</TableCell>
                        <TableCell>Entered</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentOrders.slice(0, 20).map((order) => {
                        const orderId = order.orderId || order.id || '';
                        const status = order.status || 'UNKNOWN';
                        const entered = order.enteredTime
                          ? new Date(order.enteredTime).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                          : '';
                        const isWorking = status === 'WORKING' || status === 'ACCEPTED' || status === 'PENDING_ACTIVATION' || status === 'LIVE' || status === 'PENDING' || status === 'CONFIRMED' || status === 'UNCONFIRMED' || status === 'QUEUED';
                        const isModifying = modifyingOrderId === orderId;
                        return (
                          <React.Fragment key={orderId}>
                            <TableRow>
                              <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                {String(orderId).slice(0, 12)}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={status}
                                  size="small"
                                  color={
                                    status === 'FILLED' ? 'success' :
                                    isWorking ? 'primary' :
                                    status === 'CANCELED' || status === 'REJECTED' ? 'error' :
                                    'default'
                                  }
                                />
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.75rem' }}>
                                {order.price || order.activePrice || '—'}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.75rem' }}>{entered}</TableCell>
                              <TableCell align="right">
                                {isWorking && !isModifying && (
                                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                    <Button
                                      size="small"
                                      onClick={() => startModifyOrder(order)}
                                    >
                                      Modify
                                    </Button>
                                    <Button
                                      size="small"
                                      color="error"
                                      onClick={() => handleCancelOrder(orderId)}
                                    >
                                      Cancel
                                    </Button>
                                  </Box>
                                )}
                                {isWorking && isModifying && (
                                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center' }}>
                                    <TextField
                                      size="small"
                                      value={modifyPrice}
                                      onChange={(e) => setModifyPrice(e.target.value)}
                                      sx={{ width: 80 }}
                                      placeholder="Price"
                                      InputProps={{
                                        endAdornment: (
                                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            <IconButton
                                              size="small"
                                              sx={{ p: 0.1 }}
                                              onClick={() => {
                                                const val = parseFloat(modifyPrice) || 0;
                                                setModifyPrice(String(Math.round((val + 0.01) * 100) / 100));
                                              }}
                                            >
                                              <ArrowDropUpIcon />
                                            </IconButton>
                                            <IconButton
                                              size="small"
                                              sx={{ p: 0.1, mt: -1 }}
                                              onClick={() => {
                                                const val = parseFloat(modifyPrice) || 0;
                                                setModifyPrice(String(Math.round((val - 0.01) * 100) / 100));
                                              }}
                                            >
                                              <ArrowDropDownIcon />
                                            </IconButton>
                                          </Box>
                                        ),
                                      }}
                                    />
                                    <Button
                                      size="small"
                                      color="primary"
                                      variant="contained"
                                      disabled={modifyLoading}
                                      onClick={() => handleModifyOrder(orderId)}
                                    >
                                      {modifyLoading ? <CircularProgress size={16} /> : 'Save'}
                                    </Button>
                                    <Button
                                      size="small"
                                      onClick={cancelModifyOrder}
                                    >
                                      X
                                    </Button>
                                  </Box>
                                )}
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Strategy reference */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                <strong>OCC Symbol Format</strong>
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Format: <code>UNDERLYING + YYMMDD + C/P + STRIKE(8-digit)</code>
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Example: <code>SPY240315C00150000</code> = SPY Call expiring 2024-03-15, strike $150
              </Typography>

              <Typography variant="subtitle1" gutterBottom>
                <strong>Strategy Leg Structure</strong>
              </Typography>
              <Box sx={{ pl: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  - <strong>Vertical Spread:</strong> Sell short strike, Buy long strike (same type)
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  - <strong>Short Strangle:</strong> Sell OTM call + Sell OTM put
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  - <strong>Iron Condor:</strong> Buy put wing, Sell put short, Sell call short, Buy call wing
                </Typography>
                <Typography variant="body2">
                  - <strong>Custom:</strong> Manually specify each leg with OCC symbol
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
